import axios from 'axios';

/**
 * Submit a contact to a lender with optional file attachments
 * 
 * @param {Object} contactData - Contact information
 * @param {Object} businessData - Business opportunity data
 * @param {string} lenderId - ID of the lender to submit to
 * @param {File[]} bankFiles - Array of bank statement files
 * @param {File[]} appFiles - Array of application document files
 * @param {string} [financingType='working-capital'] - Type of financing ('working-capital' or 'equipment-financing')
 * @param {string} [submissionType='api'] - Type of submission ('api' or 'email')
 * @param {Object} [additionalFiles={}] - Additional files for specific financing types
 * @param {File[]} [additionalFiles.quoteFiles] - Array of equipment quote files for equipment financing
 * @param {File[]} [additionalFiles.creditFiles] - Array of credit files for equipment financing
 * @returns {Promise<Object>} Response from the lender API
 */
export const submitToLender = async (contactData, businessData, lenderId, bankFiles = [], appFiles = [], financingType = 'working-capital', submissionType = 'api', additionalFiles = {}) => {
  try {
    // Create form data for file upload
    const formData = new FormData();
    
    // Add contact and business data
    formData.append('contactData', JSON.stringify(contactData));
    formData.append('businessData', JSON.stringify(businessData));
    formData.append('lenderId', lenderId);
    formData.append('financingType', financingType);
    formData.append('submissionType', submissionType);
    
    // Create a structure for Salesforce files
    const salesforceFiles = {
      bankFiles: [],
      appFiles: [],
      quoteFiles: [],
      creditFiles: []
    };
    
    // Track if we have any Salesforce files
    let hasSalesforceFiles = false;
    
    // Add bank statement files
    for (const file of bankFiles) {
      if (file.salesforceFile) {
        // If it's a Salesforce file, add to the salesforceFiles structure
        salesforceFiles.bankFiles.push({
          name: file.name,
          salesforceId: file.salesforceId,
          type: file.type || file.mimetype,
          size: file.size,
          source: file.source || 'ContentDocument'
        });
        hasSalesforceFiles = true;
      } else {
        // Regular file, add directly to FormData
        formData.append('bankFiles', file);
      }
    }
    
    // Add application document files
    for (const file of appFiles) {
      if (file.salesforceFile) {
        // If it's a Salesforce file, add to the salesforceFiles structure
        salesforceFiles.appFiles.push({
          name: file.name,
          salesforceId: file.salesforceId,
          type: file.type || file.mimetype,
          size: file.size,
          source: file.source || 'ContentDocument'
        });
        hasSalesforceFiles = true;
      } else {
        // Regular file, add directly to FormData
        formData.append('appFiles', file);
      }
    }
    
    // Add equipment quote files if provided (for equipment financing)
    if (additionalFiles.quoteFiles && additionalFiles.quoteFiles.length > 0) {
      for (const file of additionalFiles.quoteFiles) {
        if (file.salesforceFile) {
          // If it's a Salesforce file, add to the salesforceFiles structure
          salesforceFiles.quoteFiles.push({
            name: file.name,
            salesforceId: file.salesforceId,
            type: file.type || file.mimetype,
            size: file.size,
            source: file.source || 'ContentDocument'
          });
          hasSalesforceFiles = true;
        } else {
          // Regular file, add directly to FormData
          formData.append('quoteFiles', file);
        }
      }
    }
    
    // Add credit files if provided (for equipment financing)
    if (additionalFiles.creditFiles && additionalFiles.creditFiles.length > 0) {
      for (const file of additionalFiles.creditFiles) {
        if (file.salesforceFile) {
          // If it's a Salesforce file, add to the salesforceFiles structure
          salesforceFiles.creditFiles.push({
            name: file.name,
            salesforceId: file.salesforceId,
            type: file.type || file.mimetype,
            size: file.size,
            source: file.source || 'ContentDocument'
          });
          hasSalesforceFiles = true;
        } else {
          // Regular file, add directly to FormData
          formData.append('creditFiles', file);
        }
      }
    }
    
    // If we have any Salesforce files, add them to FormData as JSON
    if (hasSalesforceFiles) {
      console.log('Adding Salesforce files to submission:', salesforceFiles);
      formData.append('salesforceFiles', JSON.stringify(salesforceFiles));
    }
    
    // Submit to the lender
    const response = await axios.post('/api/lenders/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Lender submission error:', error);
    
    // Create a detailed error object
    const errorObj = new Error();
    
    if (error.response) {
      // Extract response status and data
      errorObj.response = {
        status: error.response.status,
        data: error.response.data || {}
      };
      
      // Set message based on response data or status
      if (error.response.data && error.response.data.message) {
        errorObj.message = error.response.data.message;
      } else {
        errorObj.message = `Server returned error ${error.response.status}`;
      }
    } else if (error.request) {
      // No response received
      errorObj.message = 'No response received from server';
      errorObj.request = error.request;
    } else {
      // Set message for other errors
      errorObj.message = error.message || 'Unknown error occurred';
    }
    
    throw errorObj;
  }
};

/**
 * Get lender match settings
 * 
 * @returns {Promise<Object>} Lender match settings
 */
export const getLenderMatchSettings = async () => {
  try {
    const response = await axios.get('/api/lenders/match/settings');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Toggle lender match enabled status
 * 
 * @param {boolean} enabled - Whether lender match should be enabled 
 * @returns {Promise<Object>} Response with updated enabled status
 */
export const toggleLenderMatch = async (enabled) => {
  try {
    const response = await axios.put('/api/lenders/match/enabled', { enabled });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get all available fields for lender criteria
 * 
 * @returns {Promise<Array>} Array of available fields
 */
export const getAvailableFields = async () => {
  try {
    const response = await axios.get('/api/lenders/match/available-fields');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Update criteria for a specific lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {string} name - Name of the lender
 * @param {Array} criteria - Array of criteria objects
 * @returns {Promise<Object>} Response with updated lender criteria
 */
export const updateLenderCriteria = async (lenderId, name, criteria) => {
  try {
    const response = await axios.post('/api/lenders/match/lender-criteria', {
      lenderId,
      name,
      criteria
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Delete a specific criterion for a lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {number} criterionIndex - Index of the criterion to delete
 * @returns {Promise<Object>} Response with deletion confirmation
 */
export const deleteCriterion = async (lenderId, criterionIndex) => {
  try {
    const response = await axios.delete(`/api/lenders/match/lender-criteria/${lenderId}/${criterionIndex}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Calculate lender match scores for a contact
 * 
 * @param {string} contactId - ID of the contact
 * @returns {Promise<Object>} Response with match scores
 */
export const calculateMatchScores = async (contactId) => {
  try {
    const response = await axios.get(`/api/lenders/match/calculate/${contactId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get active lenders available in the system
 * 
 * @param {string} [financingType='all'] - Type of financing ('all', 'working-capital', or 'equipment-financing')
 * @returns {Promise<Array>} Array of active lenders with id and name
 */
export const getActiveLenders = async (financingType = 'all') => {
  try {
    const response = await axios.get(`/api/lenders/active-lenders?financingType=${financingType}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching active lenders:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Sync active lenders with the backend
 * 
 * @param {Array} lenders - Array of lender objects with id and name
 * @param {string} [financingType='working-capital'] - Type of financing
 * @returns {Promise<Object>} Response from the lender API
 */
export const syncActiveLenders = async (lenders, financingType = 'working-capital') => {
  try {
    const response = await axios.post('/api/lenders/active-lenders/sync', { 
      lenders,
      financingType
    });
    return response.data;
  } catch (error) {
    console.error('Error syncing lenders with backend:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Reset active lenders in the backend by syncing both equipment and working capital lenders
 * 
 * @param {Array} equipmentLenders - Array of equipment financing lenders
 * @param {Array} workingCapitalLenders - Array of working capital lenders
 * @returns {Promise<Object>} Response from the lender API
 */
export const resetActiveLenders = async (equipmentLenders, workingCapitalLenders) => {
  try {
    console.log('Resetting active lenders in the backend...');
    
    // First sync working capital lenders
    console.log(`Syncing ${workingCapitalLenders.length} working capital lenders...`);
    await syncActiveLenders(
      workingCapitalLenders.filter(l => !l.id.startsWith('//')),
      'working-capital'
    );
    
    // Then sync equipment financing lenders
    console.log(`Syncing ${equipmentLenders.length} equipment financing lenders...`);
    await syncActiveLenders(
      equipmentLenders.filter(l => !l.id.startsWith('//')),
      'equipment-financing'
    );
    
    console.log('All lenders synced successfully');
    
    return { 
      success: true, 
      message: 'Lenders reset successfully',
      workingCapitalCount: workingCapitalLenders.filter(l => !l.id.startsWith('//')).length,
      equipmentCount: equipmentLenders.filter(l => !l.id.startsWith('//')).length
    };
  } catch (error) {
    console.error('Error resetting active lenders:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Update lender tier assignments
 * 
 * @param {Array} lenderTiers - Array of objects with lenderId and tier
 * @returns {Promise<Object>} Response from the lender API
 */
export const updateLenderTiers = async (lenderTiers) => {
  try {
    const response = await axios.put('/api/lenders/tiers', { lenderTiers });
    return response.data;
  } catch (error) {
    console.error('Error updating lender tiers:', error);
    throw error.response ? error.response.data : error;
  }
}; 